import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ApiError } from "~/api/types";
import {
  COLLECTION_ACTIONS,
  usePendingOrders,
} from "~/components/services/hooks/useOrdersWithPermissions";
import { useTeam } from "~/contexts/TeamProvider";
import { usePolling } from "~/hooks/usePolling";
import { orderQueries } from "~/pages/services/queryKeys";

import {
  OrderPaymentCheck,
  OrderPaymentCheckContext,
} from "./orderPaymentCheck";

// "armed" until the pending orders have loaded after the return.
type Phase = "armed" | "confirming" | "late";

/**
 * Rechecks the pending orders until the paid one is no longer pending, or
 * for a minute after they answered, then until it's gone ("late"). A failed
 * answer is rechecked too: no order is shown meanwhile, and the minute ends
 * the wait all the same.
 */
const OrderPaymentWatcher = ({
  teamId,
  phase,
  onPhase,
}: {
  teamId: number;
  phase: Phase;
  onPhase: (phase: Phase | null) => void;
}) => {
  const queryClient = useQueryClient();
  const { orders, isSuccess, isError, error } = usePendingOrders();
  const hasPending = orders.length > 0;
  // A 403 (the team got blocked) won't answer otherwise on a recheck.
  const isUnknown = isError && (error as ApiError | null)?.status !== 403;
  const isUnconfirmed = hasPending || isUnknown;

  // Not cancelling a refetch in flight: a load longer than a tick would
  // never land.
  const refetchPending = useCallback(() => {
    void queryClient.invalidateQueries(
      {
        queryKey: orderQueries.pending({ teamId, actions: COLLECTION_ACTIONS }),
      },
      { cancelRefetch: false },
    );
  }, [queryClient, teamId]);

  const { isPolling, startPolling } = usePolling(isUnconfirmed, refetchPending);

  // The paid order is in Past Orders now; the channel saying so may have
  // been lost during the load. Not cancelling the channel's reload.
  const confirm = useCallback(() => {
    void queryClient.invalidateQueries(
      { queryKey: orderQueries.lists(teamId) },
      { cancelRefetch: false },
    );
    onPhase(null);
  }, [queryClient, teamId, onPhase]);

  useEffect(() => {
    if (phase === "armed" && (isSuccess || isError)) {
      if (isUnconfirmed) {
        startPolling();
        onPhase("confirming");
      } else if (isSuccess) {
        // Paid already: Past Orders may have been read just before.
        confirm();
      } else {
        onPhase(null);
      }
    } else if (phase === "confirming" && !isPolling) {
      if (hasPending) onPhase("late");
      else confirm();
    } else if (phase === "late" && !hasPending) {
      confirm();
    }
  }, [
    phase,
    isSuccess,
    isError,
    isUnconfirmed,
    hasPending,
    isPolling,
    startPolling,
    onPhase,
    confirm,
  ]);

  return null;
};

/**
 * Back from Stripe's pay page (a full page load through /billing/order,
 * see OrderPaymentReturn), the order stays pending until its webhook lands,
 * seconds later or a minute on a cold worker, and the OrdersChannel message
 * may be lost during the load. Until then the order can be neither
 * cancelled nor paid again: a cancelled order still gets paid by its
 * webhook, and its replacement would be paid twice. Above the routes, so
 * the wait outlives leaving the services pages (not a reload).
 */
export const OrderPaymentProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { team } = useTeam();
  const location = useLocation();
  const navigate = useNavigate();
  const [check, setCheck] = useState<{ teamId: number; phase: Phase } | null>(
    null,
  );

  const teamId = team?.id;
  const isAfterPayment = Boolean(location.state?.afterOrderPayment);

  useEffect(() => {
    if (!isAfterPayment || teamId === undefined) return;

    setCheck({ teamId, phase: "armed" });

    // One-shot: history state survives reloads.
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
  }, [isAfterPayment, teamId, navigate, location.pathname, location.search]);

  // A payment made for one team isn't awaited on another.
  const isCurrent = check !== null && check.teamId === teamId;

  useEffect(() => {
    if (check && !isCurrent) setCheck(null);
  }, [check, isCurrent]);

  const handlePhase = useCallback((phase: Phase | null) => {
    setCheck((current) => (current && phase ? { ...current, phase } : null));
  }, []);

  const value: OrderPaymentCheck = !isCurrent
    ? null
    : check.phase === "late"
      ? "late"
      : "confirming";

  return (
    <OrderPaymentCheckContext.Provider value={value}>
      {children}
      {isCurrent && (
        <OrderPaymentWatcher
          teamId={check.teamId}
          phase={check.phase}
          onPhase={handlePhase}
        />
      )}
    </OrderPaymentCheckContext.Provider>
  );
};
